import { InventoryPolicy as ShopifyInventoryPolicy } from "@redotech/redo-shopify-order-common/shopify-inventory-policy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Based on Shopify's "Inventory Policy"
 * https://shopify.dev/docs/api/admin-graphql/2024-10/enums/ProductVariantInventoryPolicy
 *
 * This enum is intentionally separate from Shopify's enum to keep the Product
 * collection platform agnostic.
 */
export enum InventoryPolicy {
  /**
   * Customers can buy this product after it's out of stock.
   */
  CONTINUE = "continue",
  /**
   * Customers can't buy this product after it's out of stock.
   */
  DENY = "deny",
}

/**
 * Shopify inventory policies are uppercase
 */
export function shopifyInventoryPolicyToInventoryPolicy(
  shopifyInventoryPolicy: string,
): InventoryPolicy {
  switch (shopifyInventoryPolicy) {
    case "CONTINUE":
      return InventoryPolicy.CONTINUE;
    case "DENY":
      return InventoryPolicy.DENY;
    default:
      throw new Error(`Unknown inventory policy: ${shopifyInventoryPolicy}`);
  }
}

export const ShopifyProductImage = z.object({
  id: z.string().optional().nullable(),
  url: z.string().optional().nullable(),
});

export const ProductVariantSchema = z.object({
  id: z.string(),
  product: z.object({ id: z.string().nullish(), title: z.string().nullish() }),
  legacyResourceId: z.string(),
  title: z.string(),
  price: z.string(),
  inventoryQuantity: z.number().optional().nullable(),
  compareAtPrice: z.string().optional().nullable(),
  availableForSale: z.boolean(),
  inventoryPolicy: z.string().nullish(), // Because of a mismatch between inventory policy uppercase and lowercase we have this as a string.
  inventoryTracked: z.boolean().nullish(),
  image: z.object({ url: z.string() }).optional().nullable(),
  selectedOptions: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      productAsVariant: z.boolean().optional(),
    }),
  ),
  sku: z.string().nullish(),
  contextualPricing: z.object({
    price: z.object({ amount: z.string(), currencyCode: z.string() }),
  }),
  backorderDate: z.object({ value: z.string() }).optional().nullable(),
});

export const redoProductDataSchema = z.object({
  id: z.string(),
  legacyResourceId: z.string(),
  vendor: z.string().optional(),
  tags: z.array(z.string()),
  publishedAt: z.string().optional().nullable(),
  productType: z.string(),
  onlineStoreUrl: z.string().optional().nullable(),
  totalInventory: z.number(),
  handle: z.string(),
  title: z.string(),
  description: z.string(),
  options: z.array(
    z.object({
      name: z.string(),
      optionValues: z.array(z.object({ name: z.string() })),
    }),
  ),
  featuredMedia: z
    .object({
      preview: z
        .object({ image: z.object({ url: z.string() }).optional().nullable() })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  collections: z.object({
    nodes: z.array(z.object({ id: z.string(), title: z.string() })),
  }),
  redoExchangeCollection: z.object({ value: z.string().nullish() }).nullish(),
  redoVariantExchangeOptions: z
    .object({ value: z.string().nullish() })
    .nullish(),
  variants: z.object({ nodes: z.array(ProductVariantSchema) }),
  images: z.object({ nodes: z.array(ShopifyProductImage) }),
  priceRange: z.object({
    minVariantPrice: z.object({ amount: z.string(), currencyCode: z.string() }),
    maxVariantPrice: z.object({ amount: z.string(), currencyCode: z.string() }),
  }),
  isGiftCard: z.boolean(),
  status: z.string().optional(),
});

export type RedoProductData = z.infer<typeof redoProductDataSchema>;
export type ProductVariant = z.infer<typeof ProductVariantSchema>;

export const ProductImage = z.object({ url: z.string(), alt: z.string() });

export const VariantInfo = z.object({
  id: z.string(),
  price: z.string(),
  priceCurrencyCode: z.string().optional(),
  inventoryPolicy: createEnum(InventoryPolicy),
  inventoryQuantity: z.number(),
  inventoryTracked: z.boolean(),
  sku: z.string(),
  name: z.string(),
  quantityAvailable: z.number(),
  image: ProductImage,
  productId: z.string(),
  productName: z.string(),
  weightGrams: z.number().nullish(),
});

export type VariantInfo = z.infer<typeof VariantInfo>;

export const ProductInfo = z.object({
  id: z.string(),
  name: z.string(),
  handle: z.string().optional(),
  isGiftCard: z.boolean().optional(),
  image: ProductImage,
  description: z.string(),
  tags: z.array(z.string()),
  variants: z.array(VariantInfo),
});

export type ProductInfo = z.infer<typeof ProductInfo>;

export enum ECommercePlatform {
  SHOPIFY = "shopify",
  COMMERCE_CLOUD = "commerceCloud",
  COMMENT_SOLD = "commentsold",
  BIGCOMMERCE = "bigcommerce",
}

/**
 * Converts `123` to `gid://shopify/Product/123`
 */
export function productIdToShopifyGlobalProductId(productId: string): string {
  return `gid://shopify/Product/${productId}`;
}

/**
 * Converts `gid://shopify/Product/123` to `123`
 */
export function shopifyGlobalProductIdToProductId(
  globalProductId: string,
): string {
  const productId = globalProductId.split("/").pop();
  if (!productId) {
    throw new Error(`Invalid global product ID: ${globalProductId}`);
  }
  return productId;
}

export const getShopifyProductDetailPageUrl = (
  storeUrl: string,
  productHandle: string,
  variantId?: string,
): string => {
  const baseUrl = `https://${storeUrl}/products/${productHandle}`;
  return variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;
};

export enum ProductStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  DRAFT = "draft",
}

function transformInventoryPolicyToShopifyInventoryPolicy(
  inventoryPolicy: InventoryPolicy | ShopifyInventoryPolicy,
): ShopifyInventoryPolicy {
  const lowerCaseInventoryPolicy = inventoryPolicy.toLowerCase();
  if (lowerCaseInventoryPolicy === InventoryPolicy.CONTINUE.toLowerCase()) {
    return ShopifyInventoryPolicy.CONTINUE;
  } else if (lowerCaseInventoryPolicy === InventoryPolicy.DENY.toLowerCase()) {
    return ShopifyInventoryPolicy.DENY;
  } else {
    throw new Error(`Unknown inventory policy: ${inventoryPolicy}`);
  }
}

export const VariantExchangeDataSchema = z.object({
  productTitle: z.string(),
  status: z.string().optional(),
  tags: z.array(z.string()),
  options: z.array(
    z.object({
      position: z.number(),
      name: z.string(),
      values: z.array(z.string()),
    }),
  ),
  /** @deprecated Use images instead */
  image: z.object({ src: z.string() }),
  /**
   * This includes all images for the product, including variant images.
   */
  images: z
    .array(z.object({ url: z.string(), alt: z.string().nullish() }))
    .nullish(),
  redoExchangeCollection: z.string().optional(),
  redoVariantExchangeOptions: z.string().optional(),
  variants: z.array(
    z.object({
      id: z.string(),
      productId: z.string(),
      productTitle: z.string().optional(),
      sku: z.string().optional(),
      title: z.string(),
      displayName: z.string(),
      price: z.string(),
      image: z.string().optional(),
      inventoryQuantity: z.number(),
      inventoryTracked: z.boolean(),
      inventoryPolicy: z
        .union([
          createEnum(InventoryPolicy),
          createEnum(ShopifyInventoryPolicy),
        ])
        .transform(transformInventoryPolicyToShopifyInventoryPolicy), // These enums really need to be consolidated.
      selectedOptions: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          productAsVariant: z.boolean().optional(),
        }),
      ),
      grams: z.number().nullish(),
      backorder: z.string().nullish(),
    }),
  ),
});

export type VariantExchangeData = z.infer<typeof VariantExchangeDataSchema>;
